import { AdjustmentsService, TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { AwsAdjustment, ManualAdjustment } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box, Button, Container, Grid, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import { createRef, useContext, useEffect, useState } from 'react';
import { match, useHistory, useRouteMatch } from 'react-router';

import { HelpPanelTopicOptions } from '../../../common/Constants';
import { constants } from '../../../common/Localization';
import { ErrorWithResponse, MatchParam } from '../../../common/types';
import { HelpPanelContext } from '../../../contexts/HelpPanelContext';
import ConfirmationModal from '../../common/ConfirmationModal';
import AddEditAdjustmentItem from './AddEditAdjustmentItem';
import { AdjustmentType, ForwardRefFnType } from './interfaces';

const adjustmentsService = new AdjustmentsService();
const tcoService = new TcoService();

function EditAdjustment({ flash }: FlashContextInterface): JSX.Element {
    const { alert } = flash;
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId/adjustments/edit/:adjustmentId',
    });

    const tcoAssessmentId = match?.params.tcoAssessmentId ?? null;
    const portfolioId = match?.params.portfolioId ?? null;
    const adjustmentId = match?.params.adjustmentId ?? null;

    const history = useHistory();

    const [ loading, setLoading ] = useState(false);
    const [ updateLoading, setUpdateLoading ] = useState(false);
    const [ showCancelFriction, setShowCancelFriction ] = useState(false);
    const [ adjustmentToEdit, setAdjustmentToEdit ] = useState<ManualAdjustment>();
    const [ currency, setCurrency ] = useState('');
    
    const adjustmentsPage = `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/adjustments`;

    const awsRef = createRef<ForwardRefFnType>();
    const onPremRef = createRef<ForwardRefFnType>();
    const { panelHandler } = useContext(HelpPanelContext);
    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.ADJUSTMENTS_EDIT);
    }, [ panelHandler ]);
    
    useEffect(() => {
        const loadCurrency = async () => {
            if (!portfolioId || !tcoAssessmentId || tcoAssessmentId === 'default') {
                return;
            }
            const tcoSummary = await tcoService.getSummary(tcoAssessmentId);
            setCurrency(tcoSummary.output.summary.currency);
        };
        loadCurrency();
    }, [ portfolioId, tcoAssessmentId ]);

    const cancel = (): void => {
        history.push(adjustmentsPage);
    };

    useEffect(() => {
        const loadAdjustment = async () => {
            if (!portfolioId || !adjustmentId) {
                return;
            }
            setLoading(true);
            try {
                const adjustment = await adjustmentsService.getAdjustmentById(portfolioId, adjustmentId);
                setAdjustmentToEdit(adjustment);
            } catch (err) {
                if ((err as ErrorWithResponse).response?.data?.message?.includes('No item with the key')) {
                    alert(FlashType.error, constants.ERROR_NO_ADJUSTMENT_FOUND, constants.ERROR_LOADING_ADJUSTMENT);
                } else {
                    alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_ADJUSTMENT);
                }
            } finally {
                setLoading(false);
            }
        };
        loadAdjustment();
    }, [ portfolioId, adjustmentId, alert ]);

    const onCancelClick = (): void => {
        const awsAdjustment = awsRef.current?.getAdjustment();
        const onPremAdjustment = onPremRef.current?.getAdjustment();

        // use equals without strict type so null == undefined will evaluate to true
        if (awsAdjustment == adjustmentToEdit?.aws && onPremAdjustment == adjustmentToEdit?.onPremises) {
            cancel();
        } else {
            setShowCancelFriction(true);
        }
    };

    const validateAndSave = async (): Promise<void> => {
        if (!portfolioId || !tcoAssessmentId || tcoAssessmentId === 'default' || !adjustmentId || !adjustmentToEdit) {
            return;
        }
        const isAwsValid = await awsRef.current?.validate();
        const isOnPremValid = await onPremRef.current?.validate();

        if (!isAwsValid || !isOnPremValid) {
            return;
        }

        const awsAdjustment = awsRef.current?.getAdjustment();
        const onPremAdjustment = onPremRef.current?.getAdjustment();

        const updatedAdjustment: ManualAdjustment = {
            portfolioId,
            tcoAssessmentId,
            adjustmentId,
            createdAt: adjustmentToEdit?.createdAt,
            createdBy: adjustmentToEdit?.createdBy,
            aws: awsAdjustment as AwsAdjustment ?? undefined,
            onPremises: onPremAdjustment ?? undefined,
        };

        setUpdateLoading(true);
        try {
            if (!awsAdjustment && !onPremAdjustment) {
                await adjustmentsService.deleteAdjustments(portfolioId, [adjustmentId]);
            } else {
                await adjustmentsService.updateAdjustment(updatedAdjustment);
            }
            flash.alert(FlashType.success, constants.SUCCESS_UPDATING_ADJUSTMENT, '');
            history.push(adjustmentsPage);
        } catch (err) {
            flash.alert(FlashType.error, (err as ErrorWithResponse).message, constants.ERROR_CREATE_ADJUSTMENT);
        } finally {
            setUpdateLoading(false);
        }
    };

    return (
        <Container>
            { loading ? <Spinner/> : (
                adjustmentToEdit && (
                    <SpaceBetween size='m'>
                        <Grid gridDefinition={[{colspan: 6}, {colspan: 6}]}>
                            <AddEditAdjustmentItem
                                ref={onPremRef}
                                type={AdjustmentType.ON_PREMISES}
                                existingAdjustment={adjustmentToEdit?.onPremises}
                                currency={currency}
                            />
                            <AddEditAdjustmentItem
                                ref={awsRef}
                                type={AdjustmentType.AWS}
                                existingAdjustment={adjustmentToEdit?.aws}
                                currency={currency}
                            />
                        </Grid>
                        <Box float='right'>
                            <SpaceBetween direction='horizontal' size='m'>
                                <Button
                                    data-testid='cancelBtn'
                                    onClick={onCancelClick}
                                    disabled={updateLoading}
                                >
                                    {constants.CANCEL_BUTTON_TEXT}
                                </Button>
                                <Button
                                    data-testid='saveBtn'
                                    variant='primary'
                                    onClick={validateAndSave}
                                    loading={updateLoading}
                                >
                                    {constants.SAVE_BUTTON_TEXT}
                                </Button>
                            </SpaceBetween>
                        </Box>
                    </SpaceBetween>
                )
            )}
            { showCancelFriction ? (
                <ConfirmationModal
                    confirmButton={constants.CONTINUE_BUTTON_TEXT}
                    onConfirm={cancel}
                    onDismiss={() => { setShowCancelFriction(false); }}
                    header={constants.UNSAVED_CHANGES_WILL_BE_LOST}
                    visible
                >
                    {constants.UNSAVED_CHANGES_WILL_BE_LOST_CONTINUE}
                </ConfirmationModal>
            ) : null }
        </Container>
    );
}

export default withFlashContext(EditAdjustment);
